import tadaFanfareG from "@/assets/audio/tada-fanfare-g.wav";
import lessonYeah from "@/assets/audio/lesson-complete-yeah.wav";

type CelebrationKey = "bite" | "lesson";

const SOURCES: Record<CelebrationKey, string | null> = {
  bite: tadaFanfareG,
  lesson: lessonYeah,
};

const VOLUME = 0.5;

// Web Audio, not <audio>: once start() is called the sound renders on the audio
// thread. An <audio> element needs main-thread round trips (seek, play) to get
// going, so it stalls behind long tasks — e.g. html2canvas cloning the slide
// right after a bite completes — while the worker-driven confetti doesn't.
let audioCtx: AudioContext | null = null;
let gainNode: GainNode | null = null;
const buffers: Partial<Record<CelebrationKey, AudioBuffer>> = {};
const loading: Partial<Record<CelebrationKey, Promise<void>>> = {};
const fallbackElements: Partial<Record<CelebrationKey, HTMLAudioElement>> = {};

const UNLOCK_GESTURE_EVENTS = ["touchstart", "touchend", "click", "keydown"];

// The context is created on mount, outside a gesture, so Safari keeps it
// suspended until resumed from one (and suspends it again after backgrounding).
// Same unlock as Howler's: a silent buffer plus resume(), synchronously inside
// the gesture. Listeners stay for the tab's lifetime, like the context.
function handleUnlockGesture() {
  const ctx = audioCtx;
  if (!ctx || ctx.state === "running" || ctx.state === "closed") return;
  const source = ctx.createBufferSource();
  source.buffer = ctx.createBuffer(1, 1, 22050);
  source.connect(ctx.destination);
  source.start(0);
  ctx.resume().catch(() => {});
}

function getAudioContext(): AudioContext | null {
  if (audioCtx) return audioCtx;
  if (typeof window === "undefined" || !window.AudioContext) return null;
  audioCtx = new window.AudioContext();
  gainNode = audioCtx.createGain();
  gainNode.gain.value = VOLUME;
  gainNode.connect(audioCtx.destination);
  // Capture phase, so a handler stopping propagation can't swallow the gesture.
  UNLOCK_GESTURE_EVENTS.forEach((type) =>
    document.addEventListener(type, handleUnlockGesture, true),
  );
  return audioCtx;
}

function loadBuffer(key: CelebrationKey) {
  const src = SOURCES[key];
  if (!src || buffers[key] || loading[key]) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  loading[key] = fetch(src)
    .then((res) => res.arrayBuffer())
    .then((data) => ctx.decodeAudioData(data))
    .then((buffer) => {
      buffers[key] = buffer;
    })
    .catch((err) => {
      console.error("[celebration] load error", key, err);
      // Allow a retry on the next preload/play.
      delete loading[key];
    });
}

// Used only until the buffer has decoded (or if Web Audio is unavailable), so a
// celebration right after mount still makes a sound.
function playWithElement(key: CelebrationKey) {
  const src = SOURCES[key];
  if (!src) return;
  let audio = fallbackElements[key];
  if (!audio) {
    audio = new Audio(src);
    audio.volume = VOLUME;
    fallbackElements[key] = audio;
  }
  audio.currentTime = 0;
  audio
    .play()
    .catch((err) => console.error("[celebration] play error", key, err));
}

export function preloadCelebrationSounds() {
  (Object.keys(SOURCES) as CelebrationKey[]).forEach(loadBuffer);
}

export function playCelebrationSound(key: CelebrationKey) {
  const buffer = buffers[key];
  const ctx = audioCtx;
  if (!buffer || !ctx || !gainNode) {
    loadBuffer(key);
    playWithElement(key);
    return;
  }
  // Not running yet (Safari before the first gesture): resume() outside a
  // gesture may be refused, so play this one with the element and let the next
  // use Web Audio. Return before start() — a queued source would play again
  // once the context resumes.
  if (ctx.state !== "running") {
    ctx
      .resume()
      .catch((err) => console.error("[celebration] resume error", key, err));
    playWithElement(key);
    return;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(gainNode);
  source.start();
}
